<template>
  <LayoutPage>
    <template #title>
      <v-icon color="primary" class="mr-2">mdi-office-building-marker</v-icon>
      <v-toolbar-title class="headline">
        {{ currentFacility && currentFacility.name }} Dashboard
      </v-toolbar-title>
    </template>

    <v-row align="stretch" class="mb-3">
      <v-col md="12">
        <v-row class="w-100" style="align-items: stretch">
          <v-col>
            <InfoCard
              id="activeSmartUnits"
              v-model="activeSmartUnits.length"
              title="Active Smart Units"
              icon="fas fa-user-edit"
              :color="colors.activeSmartUnits"
              :loading="activeSmartUnitsLoading"
              :sub-label="scheduledSmartUnitsLabel"
              :details="tableType == 'activeSmartUnits'"
              @click="setTableType"
            />
          </v-col>
          <v-col>
            <InfoCard
              id="authorizedAccesses"
              v-model="countIncidents.authorized"
              title="Authorized Accesses"
              icon="fas fa-check-square"
              :color="colors.authorizedAccesses"
              :details="tableType == 'authorizedAccesses'"
              @click="setTableType"
            />
          </v-col>
          <v-col>
            <InfoCard
              id="unauthorizedAccesses"
              v-model="countIncidents.unauthorized"
              title="Incident Resolution"
              icon="fas fa-exclamation-triangle"
              :color="colors.unauthorizedAccesses"
              :details="tableType == 'unauthorizedAccesses'"
              @click="setTableType"
            />
          </v-col>
          <v-col v-if="isMissingDeviceTileVisible">
            <CardMissingTenantDevices
              :count="awaitingDeviceUnitsCount"
              :loading="awaitingDeviceUnitsLoading"
              :color="missingDeviceTileColor"
              :details="tableType === 'awaitingTenantDevices'"
              @click="handleMissingDeviceTileClick"
            />
          </v-col>
          <v-col v-if="showTaskManagement">
            <InfoCard
              id="openAndOverdueTasks"
              :value="taskInfoCard"
              icon="fa-sharp-duotone fa-light fa-clipboard-list-check"
              :color="colors.openAndOverdueTasks"
              :loading="tasksCountLoading"
              :details="tableType == 'openAndOverdueTasks'"
              title="Today's Tasks"
              @click="setTableType"
            />
          </v-col>

          <v-col>
            <InfoCard
              id="unhealthyDefenders"
              v-model="leanUnhealthyDevices.length"
              title="Manage Unhealthy Devices"
              icon="fas fa-first-aid"
              :color="colors.unhealthyDefenders"
              :loading="leanUnhealthyDevicesLoading"
              :details="tableType == 'unhealthyDefenders'"
              @click="setTableType"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-expand-transition>
        <v-col v-if="tableType" md="12">
          <DeviceInfoTable
            v-if="tableType === 'activeSmartUnits'"
            :color="colors[tableType]"
          />
          <TaskPageSwitch
            v-else-if="tableType === 'openAndOverdueTasks'"
            :color="colors[tableType]"
            :overdue-count="taskInfoCard.overdueTasks"
            @refetch-tasks="refetchTasks"
          />
          <IncidentsInfoTable
            v-else-if="tableType === 'authorizedAccesses'"
            :authorized="true"
            :color="colors[tableType]"
            @task-updated="handleIncidentTableTaskUpdate"
          />
          <IncidentsInfoTable
            v-else-if="tableType === 'unauthorizedAccesses'"
            :authorized="false"
            :color="colors[tableType]"
            @task-updated="handleIncidentTableTaskUpdate"
          />
          <UnhealthyInfoTable
            v-else-if="tableType === 'unhealthyDefenders'"
            :color="colors[tableType]"
          />
          <AwaitingDeviceInfoTable
            v-else-if="tableType === 'awaitingTenantDevices'"
            :color="missingDeviceTileColor"
            :is-revmax="isAutoEnrollment"
          />
        </v-col>
      </v-expand-transition>

      <v-col md="3">
        <RenterDemoForm class="dashboard-card" />
      </v-col>

      <v-col md="3">
        <CardRecentIncidents
          incident-type="active-units"
          class="dashboard-card"
        />
      </v-col>

      <v-col md="3">
        <CardRecentIncidents
          incident-type="vacant-units"
          class="dashboard-card"
        />
      </v-col>

      <v-col md="3">
        <CardRecentIncidents
          incident-type="concerning"
          class="dashboard-card"
        />
      </v-col>
    </v-row>

    <FeatureWrapper feature="smart_zones">
      <v-row align="stretch">
        <v-col md="3">
          <InfoCard
            id="smartZoneMotion"
            v-model="countZoneIncidents.motion"
            title="Zone Motion Incidents"
            icon="fas fa-walking"
            :color="colors.commonColor"
            background-color="#75458c"
            :details="zoneTableType == 'smartZoneMotion'"
            @click="setZoneTableType"
          />
        </v-col>
        <v-col md="3">
          <InfoCard
            id="visualVerifyZone"
            v-model="countZoneIncidents.visualVerify"
            title="+VisualVerify Zone"
            icon="mdi mdi-play"
            :color="colors.commonColor"
            background-color="#75458c"
            :details="zoneTableType == 'visualVerifyZone'"
            @click="setZoneTableType"
          />
        </v-col>

        <v-col md="3">
          <InfoCard
            id="smartZoneClimate"
            v-model="countZoneIncidents.climate"
            title="Zone Climate Incidents"
            :color="colors.commonColor"
            icon="fas fa-temperature-hot"
            background-color="#75458c"
            :details="zoneTableType == 'smartZoneClimate'"
            @click="setZoneTableType"
          />
        </v-col>
        <v-col md="3">
          <InfoCard
            v-if="zoneTrafficLoading"
            id="smartZoneTraffic"
            v-model="primaryZoneTraffic"
            title="Zone Traffic"
            icon="fas fa-users-class"
            background-color="#75458c"
            :color="colors.commonColor"
            :loading="zoneTrafficLoading"
            :details="zoneTableType == 'zoneTrafficLoading'"
            @click="setZoneTableType"
          />
          <InfoCard
            v-else-if="primarySmartZone"
            id="smartZoneTraffic"
            v-model="primaryZoneTraffic"
            :title="`Smart Zone Traffic`"
            icon="fas fa-users-class"
            background-color="#75458c"
            :color="colors.commonColor"
            :loading="zoneTrafficLoading"
            :details="zoneTableType == 'smartZoneTraffic'"
            @click="setZoneTableType"
          />
          <InfoCard
            v-else
            id="smartZoneTraffic"
            value="0"
            :title="primarySmartZoneWarning"
            icon="fas fa-exclamation-triangle"
            :color="colors.commonColor"
            background-color="warning"
            :details="zoneTableType == 'smartZoneTraffic'"
            @click="setZoneTableType"
          />
        </v-col>

        <v-expand-transition>
          <v-col v-if="zoneTableType" md="12">
            <ZoneInfoTable
              v-if="zoneTableType === 'activeSmartZones'"
              :loading="activeSmartZonesLoading"
              :zones="activeSmartZones"
            />
            <ZoneIncidentTable
              v-else-if="zoneTableType === 'smartZoneMotion'"
              incident-type="motion"
            />
            <ZoneIncidentTable
              v-else-if="zoneTableType === 'smartZoneClimate'"
              incident-type="climate"
            />
            <template v-else-if="zoneTableType === 'visualVerifyZone'">
              <FeatureWrapper feature="video_camera_integration">
                <VisualVerifyGrid
                  :itemsPerPageProp="4"
                  displayMode="detailed"
                  :showLocation="true"
                />

                <template #disabled>
                  <v-card class="pa-6" style="border-top: 4px solid #2d2e75">
                    <v-row align="center" justify="center">
                      <v-col cols="12" class="text-center">
                        <h2 class="headline">
                          Want to get started with
                          <strong>+VisualVerify</strong>?
                        </h2>
                        <p class="mt-4 mb-2">
                          Contact us at
                          <a href="mailto:marketing@storage-defender.com"
                            >marketing@storage-defender.com</a
                          >!
                        </p>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </FeatureWrapper>
            </template>

            <ZoneTrafficGraph
              v-else-if="zoneTableType === 'smartZoneTraffic'"
            />
          </v-col>
        </v-expand-transition>
      </v-row>
    </FeatureWrapper>
  </LayoutPage>
</template>

<script>
import { yesterdayStartedAt, yesterdayEndedAt } from "@tod-ui/helpers/datetime";

import LayoutPage from "@layout/LayoutPage.vue";
import IncidentsInfoTable from "./IncidentsInfoTable.vue";
import DeviceInfoTable from "./DeviceInfoTable.vue";
import TaskPageSwitch from "@components/manage_tasks/TaskPageSwitch.vue";
import UnhealthyInfoTable from "./UnhealthyInfoTable.vue";
import AwaitingDeviceInfoTable from "./AwaitingDeviceInfoTable.vue";
import InfoCard from "./InfoCard.vue";
import CardMissingTenantDevices from "./CardMissingTenantDevices.vue";
import RenterDemoForm from "./RenterDemoForm.vue";
import CardRecentIncidents from "./CardRecentIncidents.vue";
import ZoneInfoTable from "./ZoneInfoTable.vue";
import ZoneIncidentTable from "./ZoneIncidentTable.vue";
import ZoneTrafficGraph from "./ZoneTrafficGraph.vue";
import FeatureWrapper from "@layout/FeatureWrapper.vue";
import { zonePeopleCountingEvents } from "../../graphql/organizations/smartZones";
import {
  activeSmartUnits,
  activeSmartZonesDetailed,
} from "../../graphql/organizations";

import { leanUnhealthyDevices } from "../../graphql/incidents";
import { MISSING_DEVICE_SMART_UNITS_COUNT } from "../smart_units/graphql";
import {
  isAwaitingDevice,
  scheduledUnitsSubLabel,
} from "../smart_units/helper";
import useFacility from "../authentication/useFacility";
import useAccess from "../authentication/useAccess";
import useDashboardIncidents from "./useDashboardIncidents";
import usePMS from "../common/usePMS";
import {
  TODAYS_TASKS_COUNT,
  OVERDUE_TASKS_COUNT,
  TASK_TYPES,
} from "../manage_tasks/graphql";
import VisualVerifyGrid from "../smart_zones/CardVisualVerifyGrid.vue";
import { DateTime } from "luxon";

export default {
  name: "PageDashboard",
  components: {
    LayoutPage,
    FeatureWrapper,
    InfoCard,
    CardMissingTenantDevices,
    IncidentsInfoTable,
    TaskPageSwitch,
    DeviceInfoTable,
    UnhealthyInfoTable,
    AwaitingDeviceInfoTable,
    CardRecentIncidents,
    RenterDemoForm,
    ZoneInfoTable,
    ZoneIncidentTable,
    ZoneTrafficGraph,
    VisualVerifyGrid,
  },
  setup() {
    const {
      currentFacility,
      currentFacilityId,
      facilityTimeZone,
      facilityYesterdayDOW,
      facilityFeatureEnabled,
    } = useFacility();
    const { countIncidents, countZoneIncidents, fetchCountFacilityIncidents } =
      useDashboardIncidents();
    const { organizationAncestors, showTaskManagement } = useAccess();
    const { isAutoEnrollment, isMissingDeviceTileVisible } = usePMS();
    return {
      currentFacility,
      currentFacilityId,
      facilityTimeZone,
      organizationAncestors,
      facilityYesterdayDOW,
      facilityFeatureEnabled,
      countIncidents,
      countZoneIncidents,
      fetchCountFacilityIncidents,
      showTaskManagement,
      isAutoEnrollment,
      isMissingDeviceTileVisible,
    };
  },
  data() {
    return {
      taskInfoCard: {
        overdueTasks: 0,
        openTasks: 0,
      },
      taskTypes: [],
      activeSmartUnits: [],
      leanUnhealthyDevices: [],
      missingDeviceSmartUnits: [],
      tableType: null,
      zoneTableType: "smartZoneTraffic",
      activeSmartZones: [],
      isOverdue: false,
      zoneTraffic: [],
      colors: {
        openAndOverdueTasks: "#FF9E00",
        activeSmartUnits: "#0084FF",
        authorizedAccesses: "#0AC73C",
        unauthorizedAccesses: "#FF5533",
        unhealthyDefenders: "#989898",
        missingTenantDevices: "#7b1818",
        commonColor: "#2d2e75",
      },
    };
  },
  computed: {
    activeSmartUnitsLoading() {
      return this.$apollo.queries.activeSmartUnits.loading;
    },
    // "Active Smart Units" sub-label: count of the same counted units (the
    // ones behind the headline number / "View Details" table) that have a
    // future move-in date. Headline calculation itself stays untouched.
    scheduledSmartUnitsLabel() {
      if (this.activeSmartUnitsLoading) return null;
      return scheduledUnitsSubLabel(this.activeSmartUnits);
    },
    activeSmartZonesLoading() {
      return this.$apollo.queries.activeSmartZones.loading;
    },
    zoneTrafficLoading() {
      return this.$apollo.queries.zoneTraffic.loading;
    },
    leanUnhealthyDevicesLoading() {
      return this.$apollo.queries.leanUnhealthyDevices.loading;
    },
    awaitingDeviceUnitsLoading() {
      return this.$apollo.queries.missingDeviceSmartUnits?.loading;
    },
    awaitingDeviceUnitsCount() {
      const data = this.missingDeviceSmartUnits || [];
      return this.isAutoEnrollment
        ? data.filter(isAwaitingDevice).length
        : data.length;
    },
    missingDeviceTileColor() {
      return this.awaitingDeviceUnitsCount > 0 ? "#7b1818" : "#989898";
    },
    filteredTaskTypeIds() {
      return this.taskTypes
        .filter((taskType) => taskType.name !== "incident_resolution")
        .map((taskType) => taskType.id);
    },
    smartZonesLoading() {
      return (
        this.activeSmartZonesLoading ||
        this.zoneTrafficLoading ||
        !this.currentFacility?.profile
      );
    },
    primarySmartZone() {
      const primaryZoneId = this.currentFacility?.profile?.primaryZoneId;
      if (this.smartZonesLoading || !primaryZoneId) return null;

      return this.activeSmartZones.find(({ id }) => id === primaryZoneId);
    },
    primarySmartZoneWarning() {
      return this.currentFacility?.profile?.primaryZoneId
        ? "Primary Zone: No Assigned Devices"
        : "No Primary Zone Set";
    },
    primaryZoneTraffic() {
      if (this.zoneTrafficLoading || !this.primarySmartZone) return 0;

      return this.zoneTraffic.reduce((acc, event) => {
        return acc + (event.count || 0);
      }, 0);
    },
    tasksCountLoading() {
      return (
        this.$apollo.queries.todaysTasksCount.loading ||
        this.$apollo.queries.overdueTasksCount.loading ||
        this.$apollo.queries.taskTypes.loading
      );
    },
  },
  apollo: {
    activeSmartZones: {
      query: activeSmartZonesDetailed,
      skip() {
        return !this.facilityFeatureEnabled("smart_zones");
      },
      variables() {
        return {
          id: this.currentFacilityId,
          incident_statuses: ["started", "muted"],
          incident_resolution: "",
        };
      },
      // don't cache so we get the most real-time values for climate, etc.
      fetchPolicy: "no-cache",
      // filter out units w/o devices
      update: ({ smartZones }) =>
        smartZones.filter((o) => o.devices.length > 0),
    },
    zoneTraffic: {
      query: zonePeopleCountingEvents,
      skip() {
        return (
          !this.facilityFeatureEnabled("smart_zones") ||
          !this.currentFacility.profile.primaryZoneId
        );
      },
      variables() {
        return {
          id: this.currentFacility.profile.primaryZoneId,
          dateTimeFilter: {
            after: yesterdayStartedAt(this.facilityTimeZone),
            before: yesterdayEndedAt(this.facilityTimeZone),
          },
          aggregate: "day",
        };
      },
    },
    activeSmartUnits: {
      query: activeSmartUnits,
      variables() {
        return { id: this.currentFacilityId };
      },
      fetchPolicy: "no-cache",
      update: ({ smartUnits }) =>
        smartUnits.filter((o) => o.devices.length > 0),
    },
    missingDeviceSmartUnits: {
      query: MISSING_DEVICE_SMART_UNITS_COUNT,
      skip() {
        return !this.isMissingDeviceTileVisible;
      },
      variables() {
        return { id: this.currentFacilityId };
      },
      fetchPolicy: "cache-and-network",
      update: ({ smartUnits }) => smartUnits || [],
    },
    leanUnhealthyDevices: {
      query: leanUnhealthyDevices,
      variables() {
        return {
          parentId: this.currentFacilityId,
          disposition: "in_service",
          status: "lost_contact",
        };
      },
      fetchPolicy: "no-cache",
      pollInterval: 60000,
      update({ organizationDevices }) {
        // Filter only devices assigned to a zone
        return organizationDevices.filter(
          (device) =>
            device.organization.type === "zone" &&
            device.organization.responders.length > 0
        );
      },
    },
    taskTypes: {
      query: TASK_TYPES,
      variables() {
        return {
          sort: "name",
          status: "active",
        };
      },
      result({ data }) {
        this.taskTypes = data?.taskTypes || [];
      },
      error(error) {
        console.error("Error fetching task types:", error);
      },
    },
    todaysTasksCount: {
      query: TODAYS_TASKS_COUNT,
      fetchPolicy: "no-cache",
      skip() {
        return this.filteredTaskTypeIds.length === 0;
      },
      variables() {
        const today = DateTime.utc();
        return {
          parentId: this.currentFacilityId,
          dateTimeFilter: {
            after: today.startOf("day").toISO(),
            before: today.endOf("day").toISO(),
          },
          taskTypes: this.filteredTaskTypeIds,
        };
      },
      result({ data }) {
        if (data?.todaysTasksCount?.page) {
          this.taskInfoCard.openTasks = data.todaysTasksCount.page.totalCount;
          this.updateTaskInfoCard();
        }
      },
      error(error) {
        console.error("Error fetching today's tasks count:", error);
      },
    },
    overdueTasksCount: {
      query: OVERDUE_TASKS_COUNT,
      fetchPolicy: "no-cache",
      skip() {
        return this.filteredTaskTypeIds.length === 0;
      },
      variables() {
        const yesterday = DateTime.utc().minus({ days: 1 }).startOf("day"); // code to be removed

        return {
          parentId: this.currentFacilityId,
          dateTimeFilter: {
            before: yesterday.toISO(), // code to be removed
          },
          taskTypes: this.filteredTaskTypeIds,
        };
      },
      result({ data }) {
        if (data?.overdueTasksCount?.page) {
          this.taskInfoCard.overdueTasks =
            data.overdueTasksCount.page.totalCount;
          this.updateTaskInfoCard();
        }
      },
      error(error) {
        console.error("Error fetching overdue tasks count:", error);
      },
    },
  },
  beforeMount() {
    this.fetchCountFacilityIncidents(this.$apollo);
  },
  methods: {
    handleMissingDeviceTileClick(tableId) {
      if (this.awaitingDeviceUnitsCount === 0) {
        this.$router.push(`/organizations/${this.currentFacilityId}/inventory`);
      } else {
        this.setTableType(tableId);
      }
    },
    refetchTasks() {
      this.$apollo.queries.todaysTasksCount.refetch();
      this.$apollo.queries.overdueTasksCount.refetch();
    },
    setTableType(table) {
      this.tableType = this.tableType === table ? null : table;
      const currentUrl = new URL(window.location.href);
      const overdueParam = "overdue";
      this.isOverdue = this.taskInfoCard.overdueTasks > 0;
      if (this.tableType === "openAndOverdueTasks") {
        if (!currentUrl.searchParams.has(overdueParam)) {
          currentUrl.searchParams.set(
            overdueParam,
            this.isOverdue ? "true" : "false"
          );
          currentUrl.searchParams.set("date", "1w");
          currentUrl.searchParams.set("status", "all");
          window.history.pushState({}, "", currentUrl.toString());
        }
      } else {
        if (currentUrl.searchParams.has(overdueParam)) {
          currentUrl.searchParams.delete(overdueParam);
          currentUrl.searchParams.delete("date");
          currentUrl.searchParams.delete("status");
          window.history.pushState({}, "", currentUrl.toString());
        }
      }
    },
    setZoneTableType(table) {
      this.zoneTableType = this.zoneTableType === table ? null : table;
    },
    viewFacilityProfile() {
      this.$router.push({
        name: "FacilityProfile",
      });
    },
    viewSmartUnits() {
      this.$router.push({
        name: "SmartUnits",
      });
    },
    updateTaskInfoCard() {
      this.isOverdue = this.taskInfoCard.overdueTasks > 0;

      const currentUrl = new URL(window.location.href);
      const searchParams = currentUrl.searchParams;

      if (this.tableType === "openAndOverdueTasks" && this.isOverdue) {
        searchParams.set("overdue", "true");
        searchParams.set("date", "1w");
        searchParams.set("status", "all");
        window.history.pushState({}, "", currentUrl.toString());
      }
    },
    handleTaskUpdate(updateData) {
      this.refetchTasks();
      this.$emit("task-updated", updateData);
    },
    handleIncidentTableTaskUpdate() {
      this.fetchCountFacilityIncidents(this.$apollo);
    },
  },
};
</script>

<style scoped>
.dashboard-card {
  height: 100%;
}
</style>
